/**
 * The workflow canvas's own glyphs, as AmoregGTM's canvas draws them
 * (components/icons/workflow.tsx: Tabler and Material Symbols exports).
 * They draw in currentColor; size them with a class.
 *
 * @module components/workflows/canvas/icons
 */
import type { ReactNode, SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement>;

function icon(viewBox: string, body: ReactNode, displayName: string) {
  const Icon = (props: IconProps) => (
    <svg viewBox={viewBox} fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" {...props}>
      {body}
    </svg>
  );
  Icon.displayName = displayName;
  return Icon;
}

const line = { stroke: "currentColor", strokeWidth: 1.5, strokeLinecap: "round", strokeLinejoin: "round" } as const;

/** The Start step (Material Symbols "Bolt"). */
export const StepStartIcon = icon(
  "0 0 18 18",
  <path d="M9.75635 7.40613L9.6499 8.24988H13.395L6.80322 16.1552L7.49268 11.3563L7.61572 10.4999H4.43994L10.4624 1.80359L9.75635 7.40613Z" stroke="currentColor" strokeWidth="1.5" />,
  "StepStartIcon",
);

/** An open outlet's endpoint chevron. */
export const ChevronDownSmallIcon = icon(
  "0 0 10 10",
  <path transform="rotate(90 5 5)" d="M3.85437 2.70844L6.14604 5.0001L3.85437 7.29177" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />,
  "ChevronDownSmallIcon",
);

/** The drag grip (Material Symbols "Drag Indicator"). */
export const DragIcon = icon(
  "0 0 16 16",
  <>
    {[3.83, 8, 12.17].flatMap((cy) => [
      <circle key={`l${cy}`} cx="5.83" cy={cy} r="1.17" fill="currentColor" />,
      <circle key={`r${cy}`} cx="10.17" cy={cy} r="1.17" fill="currentColor" />,
    ])}
  </>,
  "DragIcon",
);

/** Collapse or expand a canvas panel (Tabler "Arrows Diagonal"). */
export const ExpandIcon = icon(
  "0 0 18 18",
  <>
    <path d="M11.9999 3.00015H14.9999V6.00015" {...line} />
    <path d="M10.5001 7.50015L15.0001 3.00015" {...line} />
    <path d="M5.99993 15.0001H2.99993V12.0001" {...line} />
    <path d="M2.99993 14.9999L7.49993 10.4999" {...line} />
  </>,
  "ExpandIcon",
);

/** Fit the canvas (Tabler "Maximize"). */
export const FitViewIcon = icon(
  "0 0 18 18",
  <>
    <path d="M3 6V4.5C3 4.10218 3.15804 3.72064 3.43934 3.43934C3.72064 3.15804 4.10218 3 4.5 3H6" {...line} />
    <path d="M3 12V13.5C3 13.8978 3.15804 14.2794 3.43934 14.5607C3.72064 14.842 4.10218 15 4.5 15H6" {...line} />
    <path d="M12 3H13.5C13.8978 3 14.2794 3.15804 14.5607 3.43934C14.842 3.72064 15 4.10218 15 4.5V6" {...line} />
    <path d="M12 15H13.5C13.8978 15 14.2794 14.842 14.5607 14.5607C14.842 14.2794 15 13.8978 15 13.5V12" {...line} />
  </>,
  "FitViewIcon",
);

/** The Condition step (Tabler "Git Branch"). */
export const GitBranchIcon = icon(
  "0 0 18 18",
  <>
    <circle cx="5.5625" cy="13.125" r="1.375" {...line} />
    <circle cx="5.5625" cy="4.875" r="1.375" {...line} />
    <circle cx="12.4375" cy="4.875" r="1.375" {...line} />
    <path d="M5.5625 6.25V11.75" {...line} />
    <path d="M6.9375 13.125H11.0625C11.4272 13.125 11.7769 12.9801 12.0348 12.7223C12.2926 12.4644 12.4375 12.1147 12.4375 11.75V8.3125" {...line} />
    <path d="M10.375 10.375L12.4375 8.3125L14.5 10.375" {...line} />
  </>,
  "GitBranchIcon",
);

/** Tidy the chart (Tabler "Layout 2"). */
export const LayoutIcon = icon(
  "0 0 18 18",
  <>
    <rect x="3.5" y="3.5" width="4.125" height="3.4375" rx="1.375" {...line} />
    <rect x="3.5" y="9.6875" width="4.125" height="4.8125" rx="1.375" {...line} />
    <rect x="10.375" y="3.5" width="4.125" height="4.8125" rx="1.375" {...line} />
    <rect x="10.375" y="11.0625" width="4.125" height="3.4375" rx="1.375" {...line} />
  </>,
  "LayoutIcon",
);

/** Next step (Tabler "Row Insert Bottom"). */
export const RowInsertIcon = icon(
  "0 0 18 18",
  <>
    <rect x="3" y="3.75" width="12" height="4.5" rx="0.75" {...line} />
    <path d="M9 11.25V14.25" {...line} />
    <path d="M10.5 12.75H7.5" {...line} />
  </>,
  "RowInsertIcon",
);

/** Zoom in; turned 45° it is the panels' close (Tabler "Plus"). */
export const ZoomInIcon = icon(
  "0 0 18 18",
  <>
    <path d="M9 3.74985V14.2499" {...line} />
    <path d="M3.75007 9H14.2501" {...line} />
  </>,
  "ZoomInIcon",
);

/** Zoom out (Tabler "Minus"). */
export const ZoomOutIcon = icon("0 0 18 18", <path d="M3.75007 9H14.2501" {...line} />, "ZoomOutIcon");
